import React, { Component } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import { reject, propEq, find } from "ramda";
import styled from "styled-components";
import { sortBy } from "ramda";

import {
  ModalDialog,
  ContentBlock,
  Button,
  Label,
  COLORS,
  BREAKPOINTS
} from "@onelogin/react-components";
import { updateCurrentNoteGrants } from "../../actions/grantsActions";
import GrantsTable from "./GrantsTable";
import SearchDropdown from "../common/SearchDropdown/SearchDropdown";

const ModalDialogContent = styled.div`
  padding: 24px;
  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    padding: 24px 0px;
    flex-grow: 1;
  }
`;

const SearchDropdownContentBlock = styled(ContentBlock)`
  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    padding: 0px 24px;
  }
`;

const DialogFooter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: ${props => (props.horizontalCenter ? "center" : "flex-end")};
  border-top: solid 1px ${COLORS.SEPARATOR_COLORS.MEDIUM};
  padding: 16px 24px;

  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    border: 1px solid ${COLORS.GRAY_COLORS.GRAY_94};
    background-color: ${COLORS.GRAY_COLORS.GRAY_98};
    padding: 12px;
    display: flex;
    justify-content: center;
  }
`;

const StyledButton = styled.div`
  margin-left: 12px;

  &:first-child {
    margin-left: 0px;
  }
  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    width: 50%;
  }
`;

const ShareModalButton = styled(Button)`
  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    width: 100%;
  }
`;

export class ShareModalContent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchTerm: "",
      userListWithSearch: this.props.userList,
      roleListWithSearch: this.props.roleList,
      userList: this.props.userList,
      roleList: this.props.roleList,
      userGranted: this.props.userGranted,
      roleGranted: this.props.roleGranted
    };
  }

  onSearch = value => {
    this.setState({
      searchTerm: value
    });

    if (value === "") {
      this.setState({
        userListWithSearch: this.state.userList,
        roleListWithSearch: this.state.roleList
      });
    } else {
      const filteredList = this.selectFilteredList(value);
      this.setState({
        userListWithSearch: filteredList.filteredUserList,
        roleListWithSearch: filteredList.filteredRoleList
      });
    }
  };

  resetSearch = () => {
    this.setState({
      searchTerm: "",
      userListWithSearch: this.state.userList,
      roleListWithSearch: this.state.roleList
    });
  };

  simplifyTerm = term => term.replace(/\s/g, "").toLowerCase();

  selectFilteredList = searchTerm => {
    const simplifiedSearchTerm = this.simplifyTerm(searchTerm);
    const filteredUserList = this.state.userList.filter(
      user =>
        (user.fullName &&
          this.simplifyTerm(user.fullName).includes(simplifiedSearchTerm)) ||
        (user.email &&
          this.simplifyTerm(user.email).includes(simplifiedSearchTerm)) ||
        (user.username &&
          this.simplifyTerm(user.username).includes(simplifiedSearchTerm))
    );
    const filteredRoleList = this.state.roleList.filter(role =>
      this.simplifyTerm(role.roleName).includes(simplifiedSearchTerm)
    );

    return {
      filteredUserList,
      filteredRoleList
    };
  };

  onSelectUser = item => {
    let newUserGranted = [
      ...this.state.userGranted,
      {
        userId: item.userId,
        fullName: item.value,
        writeAccess: false,
        email: item.email,
        username: item.username,
        expireAt: new Date().toISOString()
      }
    ];

    newUserGranted = sortBy(user => user.fullName, newUserGranted);

    let newUserList = reject(
      propEq("userId", item.userId),
      this.state.userList
    );
    this.setState({
      userGranted: newUserGranted,
      userList: newUserList,
      userListWithSearch: newUserList,
      searchTerm: "",
      roleListWithSearch: this.state.roleList
    });
  };

  onSelectRole = item => {
    let newRoleGranted = [
      ...this.state.roleGranted,
      {
        roleId: item.roleId,
        roleName: item.value,
        writeAccess: false,
        expireAt: new Date().toISOString()
      }
    ];

    newRoleGranted = sortBy(role => role.roleName, newRoleGranted);

    let newRoleList = reject(
      propEq("roleId", item.roleId),
      this.state.roleList
    );

    this.setState({
      roleGranted: newRoleGranted,
      roleList: newRoleList,
      roleListWithSearch: newRoleList,
      searchTerm: "",
      userListWithSearch: this.state.userList
    });
  };

  onShareNote = () => {
    const grants = {
      roles: this.state.roleGranted,
      users: this.state.userGranted
    };
    this.props.updateCurrentNoteGrants(this.props.match.params.id, grants);
    this.props.onCloseShareModal();
  };

  onRemoveRole = item => {
    const newRoleGranted = reject(
      propEq("roleId", item.roleId),
      this.state.roleGranted
    );
    let newRoleList = this.state.roleList;
    newRoleList.push({
      roleId: item.roleId,
      roleName: item.roleName
    });

    newRoleList = sortBy(role => role.roleName, newRoleList);

    this.setState({
      roleGranted: newRoleGranted,
      roleList: newRoleList,
      roleListWithSearch: newRoleList,
      searchTerm: "",
      userListWithSearch: this.state.userList
    });
  };

  onRemoveUser = item => {
    const newUserGranted = reject(
      propEq("userId", item.userId),
      this.state.userGranted
    );
    let newUserList = this.state.userList;
    newUserList.push({
      userId: item.userId,
      username: item.username,
      fullName: item.fullName,
      email: item.email
    });

    newUserList = sortBy(user => user.fullName, newUserList);

    this.setState({
      userGranted: newUserGranted,
      userList: newUserList,
      userListWithSearch: newUserList,
      searchTerm: "",
      roleListWithSearch: this.state.roleList
    });
  };

  onSelectUserAccess = selectedOption => {
    let newUserGranted = this.state.userGranted;
    find(propEq("userId", selectedOption.userId))(newUserGranted).writeAccess =
      selectedOption.writeAccess;
    this.setState({
      userGranted: newUserGranted
    });
  };

  onSelectRoleAccess = selectedOption => {
    let newRoleGranted = this.state.roleGranted;
    find(propEq("roleId", selectedOption.roleId))(newRoleGranted).writeAccess =
      selectedOption.writeAccess;
    this.setState({
      roleGranted: newRoleGranted
    });
  };

  onSelectUserExpiry = (selectedOption, selectedDate) => {
    let newUserGranted = this.state.userGranted;
    find(propEq("userId", selectedOption.userId))(
      newUserGranted
    ).expireAt = selectedDate;
    this.setState({
      userGranted: newUserGranted
    });
  };

  onSelectRoleExpiry = (selectedOption, selectedDate) => {
    let newRoleGranted = this.state.roleGranted;
    find(propEq("roleId", selectedOption.roleId))(
      newRoleGranted
    ).expireAt = selectedDate;
    this.setState({
      roleGranted: newRoleGranted
    });
  };

  render = () => {
    return (
      <>
        <ModalDialog.Header onClose={this.props.onCloseShareModal}>
          <FormattedMessage id={"share"} defaultMessage="Share" />
        </ModalDialog.Header>
        <ModalDialogContent>
          <SearchDropdownContentBlock>
            <Label>
              <FormattedMessage
                defaultMessage="Add people and roles to share"
                id="addPeopleAndRolesToShare"
              />
            </Label>
            <SearchDropdown
              searchTerm={this.state.searchTerm}
              userList={this.state.userListWithSearch}
              roleList={this.state.roleListWithSearch}
              onSearch={this.onSearch}
              onSelectUser={this.onSelectUser}
              onSelectRole={this.onSelectRole}
            ></SearchDropdown>
          </SearchDropdownContentBlock>
          <ContentBlock>
            <GrantsTable
              userGranted={this.state.userGranted}
              roleGranted={this.state.roleGranted}
              onRemoveRole={this.onRemoveRole}
              onRemoveUser={this.onRemoveUser}
              onSelectUserAccess={this.onSelectUserAccess}
              onSelectRoleAccess={this.onSelectRoleAccess}
              onSelectUserExpiry={this.onSelectUserExpiry}
              onSelectRoleExpiry={this.onSelectRoleExpiry}
            />
          </ContentBlock>
        </ModalDialogContent>
        <DialogFooter>
          <StyledButton>
            <ShareModalButton onClick={this.props.onCloseShareModal}>
              <FormattedMessage defaultMessage="Cancel" id="cancel" />
            </ShareModalButton>
          </StyledButton>
          <StyledButton>
            <ShareModalButton
              onClick={this.onShareNote}
              look="primary"
              data-testid="share-submit-button"
            >
              <FormattedMessage defaultMessage="Share" id="share" />
            </ShareModalButton>
          </StyledButton>
        </DialogFooter>
      </>
    );
  };
}

ShareModalContent.propTypes = {
  userGranted: PropTypes.array,
  roleGranted: PropTypes.array,
  userList: PropTypes.array,
  roleList: PropTypes.array
};

const mapStateToProps = (state, ownProps) => ({
  userGranted: state.grants.userGranted,
  roleGranted: state.grants.roleGranted,
  userList: state.grants.userList,
  roleList: state.grants.roleList,
  loading: state.grants.loading
});

const mapDispatchToProps = dispatch => ({
  updateCurrentNoteGrants: (id, grants) => {
    dispatch(updateCurrentNoteGrants(id, grants));
  }
});

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(ShareModalContent)
);
